import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  type OutboundParcel,
  type ParcelInsuranceCoverageType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import TriangleRightIcon from "@redotech/redo-icons/play_right_filled.svg";
import TriangleDownIcon from "@redotech/redo-icons/triangle-down_filled.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import {
  getFulfillmentGroupParcelMaxInsuredValues,
  NO_INSURANCE_REASON_MESSAGES,
  PARCEL_THRESHOLD_NOTE_MESSAGES,
  PARCEL_THRESHOLD_REASON,
} from "@redotech/redo-model/fulfillments/fulfillment-group-insurance";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useSlideoutConfig } from "../hooks/use-slideout-config";
import {
  type EditableInsuranceInfo,
  useInsuranceInfo,
} from "../utils/use-insurance-info";
import * as additionalFulfillmentCss from "./additional-fulfillment-settings.module.css";
import { ParcelEditSelect } from "./parcels/parcel-edit-select";

const SHIPSURANCE_T_AND_C_URL = "https://shipsurance.com/redo/terms";

function truncateCoverageAmountToCents(amount: number) {
  return Math.trunc(amount * 100) / 100;
}

function getCoverageInputDraftValue(value: string) {
  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return "";
  }

  // Allow only a draft currency amount: digits with at most one decimal point.
  if (!/^\d*\.?\d*$/.test(trimmedValue)) {
    return null;
  }

  const decimalIndex = trimmedValue.indexOf(".");
  if (decimalIndex === -1) {
    return trimmedValue;
  }

  const wholeAmount = trimmedValue.slice(0, decimalIndex);
  const decimalAmount = trimmedValue.slice(decimalIndex + 1, decimalIndex + 3);

  return `${wholeAmount}.${decimalAmount}`;
}

function getParsedCoverageAmount(value: string) {
  if (value === "" || value === ".") {
    return null;
  }

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function formatCoverageAmount(amount: number) {
  return `$${amount.toFixed(2)}`;
}

export const InsuranceSection = memo(function InsuranceSection({
  fulfillmentGroups,
  parcelBeingEditedPerGroup,
  parcelBeingEditedIndex,
  updateParcel,
  updateRows,
  everythingDisabled,
  isEditingLockedStatus,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  parcelBeingEditedPerGroup: OutboundParcel[];
  parcelBeingEditedIndex?: number;
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  isEditingLockedStatus: boolean;
}) {
  const { insuranceExpanded: expanded, setInsuranceExpanded: setExpanded } =
    useSlideoutConfig();

  const team = useRequiredContext(TeamContext);
  const insuranceInfo = useInsuranceInfo({
    parcelBeingEditedPerGroup,
    parcelBeingEditedIndex,
    groups: fulfillmentGroups,
    team,
  });

  if (!team.settings?.outboundLabels?.insuranceRatePercentage) {
    return null;
  }

  const sectionHeader = (
    <Flex
      align="center"
      cursor="pointer"
      justify="space-between"
      onClick={() => setExpanded(!expanded)}
    >
      <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
        Add insurance
      </Text>
      <RedoButton
        IconLeading={expanded ? TriangleDownIcon : TriangleRightIcon}
        size="xs"
      />
    </Flex>
  );

  if (!expanded) {
    return (
      <Flex dir="column" gap="xs">
        {sectionHeader}
      </Flex>
    );
  }

  if (!insuranceInfo.canEdit) {
    return (
      <Flex dir="column" gap="xs">
        {sectionHeader}
        <Flex
          align="center"
          cursor="pointer"
          dir="column"
          gap="md"
          justify="space-between"
          onClick={() => setExpanded(!expanded)}
        >
          <Flex
            bgColor="secondary"
            borderColor="tertiary"
            borderStyle="solid"
            borderWidth="1px"
            p="xl"
            radius="md"
          >
            <Text color="tertiary" fontSize="sm" textAlign="center">
              {NO_INSURANCE_REASON_MESSAGES[insuranceInfo.reason]}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="xs">
      {sectionHeader}
      <EditableInsuranceControls
        everythingDisabled={everythingDisabled}
        fulfillmentGroups={fulfillmentGroups}
        insuranceInfo={insuranceInfo}
        isEditingLockedStatus={isEditingLockedStatus}
        updateParcel={updateParcel}
        updateRows={updateRows}
      />
    </Flex>
  );
});

export function EditableInsuranceControls({
  fulfillmentGroups,
  insuranceInfo,
  updateParcel,
  updateRows,
  everythingDisabled,
  isEditingLockedStatus,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  insuranceInfo: EditableInsuranceInfo;
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  isEditingLockedStatus: boolean;
}) {
  const [customCoverageDraftValue, setCustomCoverageDraftValue] = useState<
    string | null
  >(null);
  const customCoverageIsSelected =
    insuranceInfo.insuranceUiSelection === "custom";
  const fullCoverageIsSelected = insuranceInfo.insuranceUiSelection === "full";
  let customCoverageAmount = insuranceInfo.insuredAmount;
  if (customCoverageDraftValue != null) {
    customCoverageAmount = getParsedCoverageAmount(customCoverageDraftValue);
  }
  const customCoverageIsAtMax =
    customCoverageIsSelected &&
    customCoverageAmount != null &&
    customCoverageAmount >= insuranceInfo.maxCoverageAmount;

  const updateInsurance = useHandler(
    (
      coverageType: ParcelInsuranceCoverageType,
      insuredAmount: number | null,
    ) => {
      const insuranceIsUnchanged =
        !insuranceInfo.mixedCustomInsurance &&
        insuredAmount === insuranceInfo.insuredAmount &&
        coverageType === insuranceInfo.coverageType;
      if (insuranceIsUnchanged) {
        return;
      }

      updateParcel({ insurance: { coverageType, insuredAmount } });
    },
  );

  const doFullCoverage = useHandler(() => {
    if (fullCoverageIsSelected) {
      return;
    }

    setCustomCoverageDraftValue(null);
    const updates = fulfillmentGroups.flatMap(
      (group): FulfillmentGroupUpdate[] => {
        const parcelMaxInsuredValues =
          getFulfillmentGroupParcelMaxInsuredValues({ group });
        const coverageChanged = group.selectedParcels?.some((parcel, index) => {
          const parcelMaxInsuredValue = parcelMaxInsuredValues[index];
          if (!parcelMaxInsuredValue) {
            throw new Error(`Missing max insured value for parcel ${index}`);
          }
          return (
            parcel.insurance?.coverageType !== "full" ||
            parcel.insurance?.insuredAmount !==
              parcelMaxInsuredValue.maxInsuredValue
          );
        });
        if (!coverageChanged) {
          return [];
        }

        return [
          {
            fulfillmentGroupId: group._id,
            selectedParcels: group.selectedParcels?.map((parcel, index) => {
              const parcelMaxInsuredValue = parcelMaxInsuredValues[index];
              if (!parcelMaxInsuredValue) {
                throw new Error(
                  `Missing max insured value for parcel ${index}`,
                );
              }

              return {
                ...parcel,
                insurance: {
                  coverageType: "full",
                  insuredAmount: parcelMaxInsuredValue.maxInsuredValue,
                },
              };
            }),
          },
        ];
      },
    );
    if (updates.length > 0) {
      updateRows(updates);
    }
  });

  const updateCustomCoverage = useHandler((newValue: string) => {
    const draftValue = getCoverageInputDraftValue(newValue);
    if (draftValue == null) {
      return;
    }
    setCustomCoverageDraftValue(draftValue);
  });
  let customCoverageCostText = "Included";
  if (
    customCoverageIsSelected &&
    insuranceInfo.customInsuranceDisplayedCost > 0
  ) {
    customCoverageCostText = `$${insuranceInfo.customInsuranceDisplayedCost.toFixed(2)}${
      insuranceInfo.isBulkEdit ? " total" : ""
    }`;
  }
  let customCoverageInputSavedValue = String(
    insuranceInfo.insuranceIncludedThreshold,
  );
  if (customCoverageIsSelected && insuranceInfo.insuredAmount != null) {
    customCoverageInputSavedValue = String(insuranceInfo.insuredAmount);
  }
  let customCoverageMaxMessage: string | null = null;
  if (customCoverageIsAtMax) {
    if (insuranceInfo.isBulkEdit) {
      customCoverageMaxMessage =
        "Coverage is capped at each package's individual maximum, so some packages may be insured for less than the amount entered.";
    } else {
      customCoverageMaxMessage = `Maximum coverage for this package is ${formatCoverageAmount(
        insuranceInfo.maxCoverageAmount,
      )}.`;
    }
  }
  const customCoverageInputValue =
    customCoverageDraftValue ?? customCoverageInputSavedValue;

  const selectCustomCoverage = useHandler(() => {
    if (customCoverageIsSelected) {
      return;
    }

    const initialCustomCoverageAmount = Math.min(
      insuranceInfo.insuranceIncludedThreshold,
      insuranceInfo.maxCoverageAmount,
    );

    setCustomCoverageDraftValue(null);
    updateInsurance("custom", initialCustomCoverageAmount);
  });

  const focusCustomCoverageInput = useHandler(() => {
    setCustomCoverageDraftValue(customCoverageInputSavedValue);
  });

  const blurCustomCoverageInput = useHandler(() => {
    const inputWasChanged =
      customCoverageDraftValue != null &&
      customCoverageDraftValue !== customCoverageInputSavedValue;
    if (!inputWasChanged) {
      setCustomCoverageDraftValue(null);
      return;
    }

    const insuredAmount = getParsedCoverageAmount(customCoverageInputValue);
    if (insuredAmount == null) {
      setCustomCoverageDraftValue(null);
      return;
    }

    const truncatedInsuredAmount = truncateCoverageAmountToCents(insuredAmount);
    setCustomCoverageDraftValue(null);
    updateInsurance("custom", truncatedInsuredAmount);
  });

  const selectDefaultCoverage = useHandler(() => {
    if (
      insuranceInfo.insuranceUiSelection === "included" &&
      !insuranceInfo.mixedCustomInsurance
    ) {
      return;
    }

    setCustomCoverageDraftValue(null);
    updateInsurance("included", null);
  });

  return (
    <Flex dir="column" gap="md" justify="flex-start" pt="md">
      <ParcelEditSelect />
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {`${fulfillmentGroups.length > 1 ? "Bulk update additional" : "Additional"} shipping insurance`}
          {insuranceInfo.mixedCustomInsurance && " (mixed)"}
        </Text>
        <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
          {`$${insuranceInfo.insuranceIncludedThreshold} included`}
        </Text>
        {insuranceInfo.insuranceCoverageReason !==
          PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD && (
          <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
            {
              PARCEL_THRESHOLD_NOTE_MESSAGES[
                insuranceInfo.insuranceCoverageReason
              ]
            }
          </Text>
        )}
      </Flex>
      <Flex dir="column" gap="lg">
        {insuranceInfo.insuranceIncludedThreshold > 0 && (
          <RedoCheckboxCard
            checked={insuranceInfo.insuranceUiSelection === "included"}
            dataTestId="insurance-included-coverage"
            disabled={everythingDisabled || isEditingLockedStatus}
            inputSize="sm"
            inputType="radio"
            layout={Layout.LEFT}
            onClick={selectDefaultCoverage}
            title={
              <Flex justify="space-between" w="full">
                <Text fontSize="xs" fontWeight="medium">
                  {`Included coverage up to $${insuranceInfo.insuranceIncludedThreshold}`}
                </Text>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  Included
                </Text>
              </Flex>
            }
          />
        )}
        {insuranceInfo.totalFullCoverageInsuranceCost > 0 &&
          insuranceInfo.groupCanBeFullyInsured && (
            <RedoCheckboxCard
              checked={fullCoverageIsSelected}
              dataTestId="insurance-full-coverage"
              disabled={everythingDisabled || isEditingLockedStatus}
              inputSize="sm"
              inputType="radio"
              layout={Layout.LEFT}
              onClick={doFullCoverage}
              title={
                <Flex justify="space-between" w="full">
                  <Text fontSize="xs" fontWeight="medium">
                    Full coverage
                  </Text>
                  <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                    ${insuranceInfo.totalFullCoverageInsuranceCost.toFixed(2)}
                    {insuranceInfo.isBulkEdit ? " total" : ""}
                  </Text>
                </Flex>
              }
            />
          )}
        <RedoCheckboxCard
          checked={customCoverageIsSelected}
          dataTestId="insurance-custom-coverage"
          disabled={everythingDisabled || isEditingLockedStatus}
          inputSize="sm"
          inputType="radio"
          layout={Layout.LEFT}
          onClick={selectCustomCoverage}
          title={
            <Flex justify="space-between" w="full">
              <Text fontSize="xs" fontWeight="medium">
                Custom coverage
              </Text>
              <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                {customCoverageCostText}
              </Text>
            </Flex>
          }
        >
          <RedoTextInput
            fullWidth
            inputMode="decimal"
            inputTypeHint="text"
            onBlur={blurCustomCoverageInput}
            onFocus={focusCustomCoverageInput}
            prefix="$"
            required
            setValue={updateCustomCoverage}
            size="sm"
            state={
              everythingDisabled || isEditingLockedStatus
                ? "disabled"
                : "default"
            }
            value={customCoverageInputValue}
          />
          {customCoverageMaxMessage && (
            <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
              {customCoverageMaxMessage}
            </Text>
          )}
        </RedoCheckboxCard>
      </Flex>
      <Flex>
        <RedoExternalLink
          fontSize="xxs"
          showIcon={false}
          text="Terms and conditions"
          textClassName={additionalFulfillmentCss.linkText}
          url={SHIPSURANCE_T_AND_C_URL}
        />
      </Flex>
    </Flex>
  );
}
